<script lang="ts" setup>
import { computed } from 'vue';

import type { MirrorsListItem } from '@modules/mirrors-list/types';
import type { ITableColumnsOptions } from '@modules/tables/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import { useI18n } from '@modules/i18n/composables';
import { useMirrorsListNamespace } from '@modules/mirrors-list/composables';
import {
  MIRRORS_LIST_PERMISSIONS,
  TRANSLATION_GROUP_MIRRORS_LIST,
} from '@modules/mirrors-list/constants';
import { useMirrorListItemDeleteMutation } from '@modules/mirrors-list/mutation';
import { useMirrorsListQuery } from '@modules/mirrors-list/queries';
import {
  MIRRORS_LIST_CREATE_JS_ROUTE_NAME,
  MIRRORS_LIST_CREATE_NO_JS_ROUTE_NAME,
} from '@modules/mirrors-list/submodules/create/constants';
import {
  MIRRORS_LIST_EDIT_JS_ROUTE_NAME,
  MIRRORS_LIST_EDIT_NO_JS_ROUTE_NAME,
} from '@modules/mirrors-list/submodules/edit/constants';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';

const { confirmDelete } = useConfirmDialogs();
const { getTranslation } = useI18n();
const { isMirrorJsNamespace, mirrorNamespaceParams } = useMirrorsListNamespace();

const editPageRouteName = computed(() => (
  isMirrorJsNamespace.value ? MIRRORS_LIST_EDIT_JS_ROUTE_NAME : MIRRORS_LIST_EDIT_NO_JS_ROUTE_NAME
));

const createRouteName = computed(() => (
  isMirrorJsNamespace.value ? MIRRORS_LIST_CREATE_JS_ROUTE_NAME : MIRRORS_LIST_CREATE_NO_JS_ROUTE_NAME
));

const tableHeader = computed(() => (isMirrorJsNamespace.value
  ? getTranslation(TRANSLATION_GROUP_MIRRORS_LIST, 'JS_HEADER').value
  : getTranslation(TRANSLATION_GROUP_MIRRORS_LIST, 'NO_JS_HEADER').value));

const addBtnLabel = computed(() => (isMirrorJsNamespace.value
  ? getTranslation(TRANSLATION_GROUP_MIRRORS_LIST, 'JS_ADD_BTN').value
  : getTranslation(TRANSLATION_GROUP_MIRRORS_LIST, 'NO_JS_ADD_BTN').value));

const {
  data,
  isLoading,
  isFetching,
  tableState,
  updateTableState,
  refreshTableData,
} = useTableData(useMirrorsListQuery, mirrorNamespaceParams);

const columnOptions: ITableColumnsOptions = {
  enabled: {
    localization: {
      translationPathForTruthyValue: 'admin.mirrors.mirrors_list_js.status_enabled',
      translationPathForFalsyValue: 'admin.mirrors.mirrors_list_js.status_disabled',
    },
  },
  domain: {
    defaultMobilePreview: true,
  },
};

const { mutate: removeItem } = useMirrorListItemDeleteMutation();

function remove(event: Event, item: MirrorsListItem): void {
  confirmDelete({
    event,
    header: isMirrorJsNamespace.value
      ? getTranslation(TRANSLATION_GROUP_MIRRORS_LIST, 'JS_REMOVE_MESSAGE').value
      : getTranslation(TRANSLATION_GROUP_MIRRORS_LIST, 'NO_JS_REMOVE_MESSAGE').value,
    message: isMirrorJsNamespace.value
      ? getTranslation(TRANSLATION_GROUP_MIRRORS_LIST, 'JS_REMOVE_TITLE').value
      : getTranslation(TRANSLATION_GROUP_MIRRORS_LIST, 'NO_JS_REMOVE_TITLE').value,
    acceptLabel: isMirrorJsNamespace.value
      ? getTranslation(TRANSLATION_GROUP_MIRRORS_LIST, 'JS_ANSWER_YES').value
      : getTranslation(TRANSLATION_GROUP_MIRRORS_LIST, 'NO_JS_ANSWER_YES').value,
    rejectLabel: isMirrorJsNamespace.value
      ? getTranslation(TRANSLATION_GROUP_MIRRORS_LIST, 'JS_ANSWER_NO').value
      : getTranslation(TRANSLATION_GROUP_MIRRORS_LIST, 'NO_JS_ANSWER_NO').value,
    accept: () => removeItem(item.id),
  });
}
</script>

<template>
  <VCard v-data-qa-auto-id="'MirrorsListPage'" type="table">
    <VTable
      :data="data"
      :title-t-path="tableHeader"
      :loading="isLoading"
      :is-fetching="isFetching"
      :state="tableState"
      :columns-options="columnOptions"
      @state-update="updateTableState"
      @refresh="refreshTableData"
    >
      <template #recordAction="{ listItem }">
        <VIconButton
          v-if="$hasPermission(MIRRORS_LIST_PERMISSIONS.EDIT)"
          icon="pi pi-file-edit"
          :to="{ name: editPageRouteName, params: { mirrorsNamespace: mirrorNamespaceParams, id: listItem.id } }"
        />
        <VIconButton
          v-if="$hasPermission(MIRRORS_LIST_PERMISSIONS.DELETE)"
          icon="pi pi-trash"
          @click="remove($event, listItem)"
        />
      </template>
      <template
        v-if="$hasPermission(MIRRORS_LIST_PERMISSIONS.CREATE)"
        #footerButtons
      >
        <VButton
          :label="addBtnLabel"
          :to="{ name: createRouteName, params: { mirrorsNamespace: mirrorNamespaceParams } }"
        />
      </template>
    </VTable>
  </VCard>
</template>
